import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DocumentModel } from '@data-layer/models/documents';
import { enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { CompaniesService } from '@services/companies.service';
import { Router } from '@angular/router';
import { DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { take, switchMap, tap, finalize, takeUntil } from 'rxjs/operators';
import { enumDigitalSignState } from '@data-layer/enums/digital-signs/enumDigitalSignState';
import { enumPaymentState } from '@data-layer/enums/payments/enumPaymentState';
import { IorContactMatchHelper } from '@helpers/ior-contact-match.helper';
import { PoaCompletenessHelper } from '@helpers/poa-completeness.helper';
import { CompanyModel } from '@data-layer/models/companies';
import { DocumentsRepository } from '@data-layer/repositories/documents.repository';
import { DigitalSignModel } from '@data-layer/repositories/digital-signs.repository';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { FormControl } from '@angular/forms';
import { combineLatest, Subject } from 'rxjs';
import { OrganizationsService } from '@services/organizations.service';
import { Store } from '@ngxs/store';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { ProductDetailsModel } from '@data-layer/models/common';
import { FetchProductsByCompany, SharedSelectors } from '@app/shared/shared-state';
import { PoaPriceHelper } from '@helpers/poa-price.helper';

@Component({
  selector: 'company-power-attorney',
  templateUrl: './company-power-attorney.component.html',
  styleUrls: ['./company-power-attorney.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.poa-compact]': 'useCompactLayout' },
})
export class CompanyPowerAttorneyComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isShowSubheader: boolean = true;
  @Input() public isShowDescription: boolean;
  @Input() public poaDocument: DocumentModel;
  @Input() public isLoadingCompanyPoa: boolean;
  @Input() public isTablet: boolean;
  @Input() public companyId: number;
  // #13318 — the organization the company belongs to (or will belong to, on the create page):
  // the POA price is resolved from it while there is no company id yet
  @Input() public organizationId: number;
  @Input() public isRegistrationApproved: boolean = false;
  @Input() public identifierQualifier: enumIdentifierQualifier;
  @Input() public allowManualBeforeApproval: boolean = true;
  @Input() public enablePoaSelection: boolean = false;
  @Input() public selectedPoaVariant: 'digital' | 'manual' | null = null;
  @Input() public poaPurchaseReturnUrl: string | null = null;
  @Input() public isCompactLayout: boolean = false;
  @Input() public useCompactLayout: boolean = false;
  // #13260 — override for hosts owning the company form (edit-company: reflects unsaved edits).
  // When not bound, the component computes completeness itself from the company input.
  @Input() public isRequiredFieldsComplete: boolean | undefined = undefined;
  // #13260 — hosts without a company form (bond purchase, ES POA sidebar) pass the persisted model
  @Input() public company: CompanyModel | null = null;
  // #12642 — company POA can be deleted/cancelled only from the company page; hosts outside it pass false
  @Input() public canRemovePoa: boolean = true;

  @Output() public uploadDocument: EventEmitter<File> = new EventEmitter<File>();
  @Output() public requestSaveBeforeSign: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deleteDocument: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public updateDocumentState: EventEmitter<enumDocumentState> = new EventEmitter<enumDocumentState>();
  @Output() public selectedPoaVariantChange: EventEmitter<'digital' | 'manual' | null> = new EventEmitter<'digital' | 'manual' | null>();
  @Output() public poaAvailabilityChange: EventEmitter<{ digital: DocumentModel | null; manual: DocumentModel | null }> = new EventEmitter<{
    digital: DocumentModel | null;
    manual: DocumentModel | null;
  }>();

  public poaFileUrl: string = '';

  // #13213 — POA price comes from the backend product config; the fee line is hidden until it arrives
  public poaPrice: number | null = null;

  public isInitialPoaLoading: boolean = true;
  public isDigitalSignLoading: boolean = false;
  public isDigitalSignInProgress: boolean = false;
  public isDigitalPaymentPending: boolean = false;
  // #13490 — paid POA the provider failed to send (FailedToSend): wait for the e-mail, no re-purchase
  public isDigitalSignFailedToSend: boolean = false;
  // #13212 — POA already signed/paid → IOR-match validation is skipped (read by EditCompanyComponent
  // to gate the digital-sign button consistently on the read-only company-view page too).
  public isPoaGrandfathered: boolean = false;
  public digitalPoaDocument: DocumentModel | null = null;
  public manualPoaDocument: DocumentModel | null = null;
  public confirmCancelDigitalPoa: boolean = false;
  public poaVariantControl: FormControl<'digital' | 'manual' | null> = new FormControl<'digital' | 'manual' | null>(null);
  public isWaitingForSave: boolean = false;
  private companyDocuments: DocumentModel[] = [];
  private destroy$: Subject<void> = new Subject<void>();
  private lastDigitalSignId: number | null = null;
  private lastDigitalSignDocumentId: number | null = null;

  @HostBinding('class.poa-compact')
  public get poaCompactClass(): boolean {
    return this.isCompactLayout;
  }

  @HostBinding('class.poa-variant-selector')
  public get poaVariantSelectorClass(): boolean {
    return this.canShowPoaVariantSelector;
  }

  // Determines if digital POA flow is available based on identifier qualifier (EIN/SSN only)
  public get isDigitalPoaAvailable(): boolean {
    return [enumIdentifierQualifier.EIN, enumIdentifierQualifier.SSN].includes(this.identifierQualifier ?? enumIdentifierQualifier.EIN);
  }

  public get isManualOnlyFlow(): boolean {
    return !this.isDigitalPoaAvailable;
  }

  public get isPoaPriceLoaded(): boolean {
    return this.poaPrice !== null;
  }

  public get poaPriceDisplay(): string {
    return this.poaPrice === null ? '' : PoaPriceHelper.formatPrice(this.poaPrice);
  }

  public get canAccessManualBeforeApproval(): boolean {
    return this.allowManualBeforeApproval && this.isManualOnlyFlow;
  }

  public get powerOfAttorneyRulesTranslationKey(): string | null {
    if (this.isDigitalPoaAvailable) {
      return 'COMPANY.POWER_OF_ATTORNEY_RULES';
    }
    return 'COMPANY.POWER_OF_ATTORNEY_RULES_MANUAL_ONLY';
  }

  // Determines if manual POA section should be shown (for backward compatibility with legacy documents)
  public get shouldShowManualPoaSection(): boolean {
    // Manual-only flow: show document or loading state
    if (this.isManualOnlyFlow) {
      return !!this.manualPoaDocument || this.isLoadingCompanyPoa;
    }
    // Digital-only flow: only show if legacy manual document exists (no upload capability)
    return !!this.manualPoaDocument;
  }

  public get canShowPoaVariantSelector(): boolean {
    return this.enablePoaSelection && (!!this.digitalPoaDocument || !!this.manualPoaDocument);
  }

  public get isDigitalRadioDisabled(): boolean {
    return !this.digitalPoaDocument;
  }

  public get isManualRadioDisabled(): boolean {
    return !this.manualPoaDocument;
  }

  public get shouldBlockManualPoa(): boolean {
    return (
      this.isDigitalSignInProgress ||
      this.isDigitalPaymentPending ||
      this.isDigitalSignFailedToSend ||
      (!this.manualPoaDocument && !!this.digitalPoaDocument)
    );
  }

  public get isDigitalSignButtonDisabled(): boolean {
    return !this.isRequiredFieldsCompleteResolved || this.isDigitalSignLoading || this.isWaitingForSave || this.isDigitalSignFailedToSend;
  }

  public get shouldShowRequiredFieldsHint(): boolean {
    return (
      !this.isRequiredFieldsCompleteResolved &&
      !this.isDigitalSignInProgress &&
      !this.isDigitalPaymentPending &&
      !this.isDigitalSignFailedToSend
    );
  }

  // #13260 — the required-fields hint links to the IOR profile page on hosts where the
  // company form is not available in place (internal-gate mode).
  public get showIorProfileHintLink(): boolean {
    return this.isRequiredFieldsComplete === undefined && !!this.company;
  }

  public get requiredFieldsHintTooltipKey(): string {
    return this.showIorProfileHintLink
      ? 'COMPANY.DIGITAL-SIGNATURE.FILL_REQUIRED_HINT_IOR_PROFILE_TOOLTIP'
      : 'COMPANY.DIGITAL-SIGNATURE.FILL_REQUIRED_HINT';
  }

  // #13260 — host override wins (edit-company); otherwise compute from the persisted model.
  // Hosts binding neither input keep the historical always-enabled behavior.
  private get isRequiredFieldsCompleteResolved(): boolean {
    if (this.isRequiredFieldsComplete !== undefined) {
      return this.isRequiredFieldsComplete;
    }
    if (!this.company) {
      return true;
    }
    return PoaCompletenessHelper.isCompanyPoaReady({
      company: this.company,
      hasPhotoIdDocument: this.hasPhotoIdDocument,
      hasSs4Document: this.hasSs4Document,
      controllingPersonScanPersonIds: this.controllingPersonScanPersonIds,
      user: this.companiesService.getUser(),
      isPoaGrandfathered: this.isPoaGrandfathered,
    });
  }

  private get hasPhotoIdDocument(): boolean {
    return this.companyDocuments.some((document: DocumentModel) => document.type === enumDocumentType.ID_DOCUMENT);
  }

  // #13356 — the company already has an SS-4 (type SS4) among its documents
  private get hasSs4Document(): boolean {
    return this.companyDocuments.some((document: DocumentModel) => document.type === enumDocumentType.SS4);
  }

  // #13269 — person ids that already have an uploaded State ID scan (type 112)
  private get controllingPersonScanPersonIds(): number[] {
    return this.companyDocuments
      .filter(
        (document: DocumentModel) =>
          document.type === enumDocumentType.CONTROLLING_PERSON_STATE_ID && document.state === enumDocumentState.UPLOADED,
      )
      .map((document: DocumentModel) => document.relatedEntityId);
  }

  constructor(
    private companiesService: CompaniesService,
    private router: Router,
    private digitalSignsRepository: DigitalSignsRepository,
    private documentsRepository: DocumentsRepository,
    private cdr: ChangeDetectorRef,
    private organizationsService: OrganizationsService,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.initPoaVariantControl();
    this.initPoaPrice();

    const companyId = this.getCompanyId();
    if (companyId) {
      this.companiesService
        .getBrokerPoaFileUrl(companyId)
        .pipe(take(1))
        .subscribe((url: string) => {
          this.poaFileUrl = url;
          this.cdr.markForCheck();
        });
    } else {
      const organizationId = this.organizationsService.getOrganization()?.id;
      if (organizationId) {
        this.organizationsService
          .getBrokerPoaFileUrl(organizationId)
          .pipe(take(1))
          .subscribe((url: string) => {
            this.poaFileUrl = url;
            this.cdr.markForCheck();
          });
      }
    }

    if (!companyId) {
      this.markInitialPoaLoaded();
      return;
    }
    this.digitalSignsRepository
      .list(companyId)
      .pipe(take(1))
      .subscribe((items) => {
        const last = (items || []).sort((a, b) => b.id - a.id)[0];
        this.updateDigitalSignState(last);
        // Load all company documents and compute digital/manual docs
        this.loadCompanyDocuments(companyId);
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const companyId = this.getCompanyId();
    // For new companies without a persisted ID, keep manualPoaDocument in sync
    // with the input poaDocument (in-memory upload flow).
    if ('poaDocument' in changes) {
      // Sync when no companyId OR when poaDocument is in-memory (id is null)
      // OR when poaDocument becomes null and manualPoaDocument was in-memory
      const isInMemoryDocument = this.poaDocument && !this.poaDocument.id;
      const wasInMemoryDocument = this.manualPoaDocument && !this.manualPoaDocument.id;
      if (!companyId || isInMemoryDocument || (this.poaDocument === null && wasInMemoryDocument)) {
        this.manualPoaDocument = this.poaDocument || null;
      }
    }

    // For existing companies, refresh documents from the backend when manual POA
    // upload/delete has finished (isLoadingCompanyPoa true -> false).
    if (companyId && 'isLoadingCompanyPoa' in changes) {
      const prev = changes['isLoadingCompanyPoa']?.previousValue;
      const curr = changes['isLoadingCompanyPoa']?.currentValue;

      if (prev && !curr) {
        this.loadCompanyDocuments(companyId);
      }
    }

    if ('selectedPoaVariant' in changes) {
      this.syncPoaVariantControl();
    }

    // #13213 — a new company gets its id after the first save; hide the possibly foreign price
    // and fetch the one for the current company
    // #13318 — the create page resolves its organization asynchronously, so the price has to be
    // fetched again once that id arrives
    const isPriceContextChanged: boolean =
      ('companyId' in changes && !changes['companyId'].firstChange) ||
      ('organizationId' in changes && !changes['organizationId'].firstChange);

    if (isPriceContextChanged) {
      this.poaPrice = null;
      this.fetchPoaPrice();
    }

    this.cdr.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCompanyDocuments(companyId: number): void {
    this.documentsRepository
      .getCompanyDocuments(companyId, false)
      .pipe(take(1))
      .subscribe({
        next: (docs) => {
          const documents: DocumentModel[] = docs || [];
          this.companyDocuments = documents;

          // Digital POA (only when DS is completed and documentId provided)
          this.digitalPoaDocument = this.lastDigitalSignDocumentId
            ? documents.find((d) => d.id === this.lastDigitalSignDocumentId) || null
            : null;

          // Manual POA: POWER_OF_ATTORNEY doc that is not the digital one
          this.manualPoaDocument =
            documents.find(
              (d) => d.type === enumDocumentType.POWER_OF_ATTORNEY && d.id !== (this.digitalPoaDocument ? this.digitalPoaDocument.id : -1),
            ) || null;

          this.poaAvailabilityChange.emit({
            digital: this.digitalPoaDocument,
            manual: this.manualPoaDocument,
          });

          if (this.enablePoaSelection) {
            let next: 'digital' | 'manual' | null = null;

            if (this.digitalPoaDocument) {
              next = 'digital';
            } else if (this.manualPoaDocument) {
              next = 'manual';
            } else {
              next = null;
            }

            if (next !== this.selectedPoaVariant) {
              this.selectedPoaVariant = next;
              this.selectedPoaVariantChange.emit(next);
              this.syncPoaVariantControl();
            }
          } else {
            this.syncPoaVariantControl();
          }

          this.markInitialPoaLoaded();
          this.cdr.markForCheck();
        },
        error: () => {
          this.markInitialPoaLoaded();
        },
      });
  }

  protected readonly enumDocumentType = enumDocumentType;

  public onPoaVariantChange(variant: 'digital' | 'manual'): void {
    if (!this.enablePoaSelection) {
      return;
    }

    if (this.selectedPoaVariant !== variant) {
      this.selectedPoaVariant = variant;
      this.selectedPoaVariantChange.emit(variant);
      this.syncPoaVariantControl();
    }
  }

  public onManualPoaUpload(event: Event, inputEl: HTMLInputElement): void {
    const target = event.target as HTMLInputElement;
    const file = target?.files && target.files.length ? target.files[0] : null;
    if (file) {
      this.uploadDocument.emit(file);
      inputEl.value = '';
    }
  }

  public onManualPoaStateUpdate(state: enumDocumentState): void {
    if (this.manualPoaDocument) {
      // Keep local manual POA document state in sync so the uploader
      // stops treating it as SCAN_PENDING once backend scanning is done.
      this.manualPoaDocument = {
        ...this.manualPoaDocument,
        state,
      };
    }

    // Propagate state change to the parent so companyDocuments stay in sync.
    this.updateDocumentState.emit(state);

    this.cdr.markForCheck();
  }

  public onDigitalSignClick(): void {
    // Always save/update company before proceeding with digital sign
    // This ensures any form changes are persisted before redirecting
    this.isWaitingForSave = true;
    this.cdr.markForCheck();
    this.requestSaveBeforeSign.emit();
  }

  public proceedAfterSave(newCompanyId: number): void {
    this.isWaitingForSave = false;
    if (newCompanyId) {
      this.companyId = newCompanyId;
    }
    this.cdr.markForCheck();
    this.initiateDigitalSign();
  }

  public cancelWaitingForSave(): void {
    this.isWaitingForSave = false;
    this.cdr.markForCheck();
  }

  private initiateDigitalSign(): void {
    const companyId = this.getCompanyId();
    if (!companyId || this.isDigitalSignLoading) {
      return;
    }
    this.isDigitalSignLoading = true;

    const source$ = this.lastDigitalSignId
      ? this.digitalSignsRepository.getById(this.lastDigitalSignId)
      : this.digitalSignsRepository.create(companyId).pipe(switchMap((id: number) => this.digitalSignsRepository.getById(id)));

    source$.pipe(take(1)).subscribe({
      next: () => {
        const commands: any[] = ['/companies', 'account-settings', 'company-view', companyId, 'poa-purchase'];
        const extras = this.poaPurchaseReturnUrl ? { queryParams: { returnUrl: this.poaPurchaseReturnUrl } } : undefined;

        this.router.navigate(commands, extras);
        this.isDigitalSignLoading = false;
      },
      error: () => {
        this.isDigitalSignLoading = false;
      },
    });
  }

  public openCancelDigitalPoaConfirm(): void {
    if (this.isDigitalSignLoading) {
      return;
    }
    this.confirmCancelDigitalPoa = true;
    this.cdr.markForCheck();
  }

  public onConfirmCancelDigitalPoa(): void {
    const companyId = this.getCompanyId();
    if (!companyId || !this.lastDigitalSignId || this.isDigitalSignLoading) {
      this.confirmCancelDigitalPoa = false;
      this.cdr.markForCheck();
      return;
    }
    this.isDigitalSignLoading = true;
    this.digitalSignsRepository
      .delete(this.lastDigitalSignId)
      .pipe(
        switchMap(() => this.digitalSignsRepository.list(companyId)),
        take(1),
        tap((items) => {
          const last = (items || []).sort((a, b) => b.id - a.id)[0];
          this.updateDigitalSignState(last);
          this.loadCompanyDocuments(companyId);
          this.confirmCancelDigitalPoa = false;
        }),
        finalize(() => {
          this.isDigitalSignLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        error: () => {
          this.confirmCancelDigitalPoa = false;
        },
      });
  }

  // #13318 — 0 is not a valid id for the backend (404), so it is treated as "no company/organization"
  private getCompanyId(): number | null {
    return this.companyId || null;
  }

  private getOrganizationId(): number | null {
    return this.organizationId || null;
  }

  private updateDigitalSignState(last: DigitalSignModel | undefined): void {
    this.lastDigitalSignId = last?.id ?? null;
    this.lastDigitalSignDocumentId = last?.documentId ?? null;
    this.isDigitalSignInProgress = !!last && [enumDigitalSignState.Sending, enumDigitalSignState.Sent].includes(last.state);
    this.isDigitalPaymentPending =
      !!last && last.state === enumDigitalSignState.Requested && last.paymentState === enumPaymentState.PENDING && !last.documentId;
    this.isDigitalSignFailedToSend = !!last && last.state === enumDigitalSignState.FailedToSend;
    this.isPoaGrandfathered = IorContactMatchHelper.isPoaGrandfathered(last);
  }

  private initPoaPrice(): void {
    // #13318 — a price fetched without a company lives in its own store slot, so both are watched
    // and the one matching the current context is used
    combineLatest([
      this.store.select(SharedSelectors.getPoaProductDetails),
      this.store.select(SharedSelectors.getPoaProductDetailsByOrganization),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([byCompany, byOrganization]: [ProductDetailsModel | null, ProductDetailsModel | null]) => {
        const companyId: number | null = this.getCompanyId();
        const details: ProductDetailsModel | null = companyId === null ? byOrganization : byCompany;
        this.poaPrice = PoaPriceHelper.extractPrice(details, companyId, this.getOrganizationId());
        this.cdr.markForCheck();
      });

    this.fetchPoaPrice();
  }

  private fetchPoaPrice(): void {
    // #13318 — on the create-company page there is no company id yet; the request goes without it
    // and the backend prices the company that would be created in this organization
    const companyId: number | null = this.getCompanyId();
    const organizationId: number | null = this.getOrganizationId();

    if (companyId === null && organizationId === null) {
      return;
    }

    // The component can be mounted more than once on a page (e.g. edit-company view/edit sections)
    // — skip the request when the store already holds the price for the same company/organization.
    // The store emits nothing when no request is dispatched, so the price is read from the cached
    // entry here: ngOnChanges clears it before refetching and would otherwise leave it hidden.
    const cached: ProductDetailsModel | null = this.store.selectSnapshot(
      companyId === null ? SharedSelectors.getPoaProductDetailsByOrganization : SharedSelectors.getPoaProductDetails,
    );
    if (cached !== null && PoaPriceHelper.isSameContext(cached, companyId, organizationId)) {
      this.poaPrice = PoaPriceHelper.extractPrice(cached, companyId, organizationId);
      return;
    }

    this.store.dispatch(new FetchProductsByCompany({ productCode: enumBillItemProductCode.POA, companyId, organizationId }));
  }

  private initPoaVariantControl(): void {
    this.poaVariantControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((variant) => {
      if (!variant || variant === this.selectedPoaVariant) {
        return;
      }
      this.onPoaVariantChange(variant);
    });
    this.syncPoaVariantControl();
  }

  private syncPoaVariantControl(): void {
    this.poaVariantControl.setValue(this.selectedPoaVariant, { emitEvent: false });
  }

  private markInitialPoaLoaded(): void {
    if (!this.isInitialPoaLoading) {
      return;
    }
    this.isInitialPoaLoading = false;
    this.cdr.markForCheck();
  }
}
